import {Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {HttpClient} from '@angular/common/http';
import {catchError, map, Observable, of, ReplaySubject, switchMap, throwError} from 'rxjs';
import {AuthUtils} from './auth.utils';
import {
    AuthorizationPasswordService,
    AuthorizationService,
    LoginRequest,
    PasswordResetRequest,
    SendResetLinkRequest,
    UserResource
} from 'marida';
import {AbstractControl, FormGroup} from '@angular/forms';



@Injectable()
export class AuthService {
    private _authenticated = false;

    private _user: ReplaySubject<UserResource> = new ReplaySubject<UserResource>(1);
    private observableUser: Observable<any> | null | undefined;
    private userSaved: any = null;


    constructor(
        public translateService: TranslateService,
        private _httpClient: HttpClient,
        private _accessService: AuthorizationService,
        private _authPassService: AuthorizationPasswordService
    ) {
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for access token
     */
    set accessToken(token: string) {
        console.log('Access token was set up');
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        // @ts-ignore
        return localStorage.getItem('accessToken') && (localStorage.getItem('accessToken') !== 'undefined') ? localStorage.getItem('accessToken') : '';
    }

    /**
     * Setter & getter for access token
     */
    set refreshToken(token: string) {
        localStorage.setItem('refreshToken', token);
    }

    get refreshToken(): string {
        return localStorage.getItem('refreshToken') ?? '';
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Forgot password
     *
     * @param email
     */
    forgotPassword(email: string): Observable<any> {

        const resetRequest: SendResetLinkRequest = {
            email: email
        };

        return this._authPassService.sendResetLink(resetRequest).pipe(
            switchMap(response =>
                // Return a new observable with the response
                of(response)
            )
        );
    }

    public changePassword(data: { email: any; token: any; password: any; passwordConfirmation: any }): Observable<any> {
        const resetRequest: PasswordResetRequest = {
            email: data.email,
            token: data.token,
            password: data.password,
            passwordConfirmation: data.passwordConfirmation,
        };

        return this._authPassService.reset(resetRequest).pipe(
            switchMap(response =>
                // Return a new observable with the response
                of(response)
            )
        );
    }

    /**
     * Reset password
     *
     * @param password
     */
    resetPassword(password: string): Observable<any> {
        return this._httpClient.post('api/auth/reset-password', password);
    }

    /**
     * Sign in
     *
     * @param credentials
     */
    signIn(credentials: { email: string; password: string }): Observable<any> {
        // Throw error, if the user is already logged in
        if (this._authenticated) {
            return throwError('User is already logged in.');
        }

        // @ts-ignore
        const credential: LoginRequest = {
            email: credentials.email,
            password: credentials.password
        };

        return this._accessService.loginUser(credential).pipe(
            switchMap((response) => {


                // Store the access token in the local storage
                this.accessToken = response.accessToken;

                // Store the refresh token in the local storage
                this.refreshToken = response.refreshToken;

                // Set the authenticated flag to true
                this._authenticated = true;

                // Store the user on the user service
                // this._userService.user = response.user;

                // Return a new observable with the response
                return of(response);
            })
        );
    }

    /**
     * Refresh token
     */
    tryToRefreshToken(): Observable<any> {
        return this._httpClient.post('/api/auth/refresh-token', {
            refreshToken: this.refreshToken
        }).pipe(
            catchError(() => {
                    // Return false @todo: Delete local storage and setup _authenticated
                    this._authenticated = false;
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('refreshToken');
                    return of(false);
                }
            ),
            switchMap((response: any) => {
                if (response === false) {
                    this._authenticated = false;
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('refreshToken');
                    return of(false);
                } else {
                    this.accessToken = response.accessToken;
                    this.refreshToken = response.refreshToken;
                    return of(true);
                }
            })
        );
    }


    /**
     * Sign out
     */
    signOut(): Observable<any> {
        // Remove the access token from the local storage
        localStorage.removeItem('accessToken');

        // Set the authenticated flag to false
        this._authenticated = false;

        // Return the observable
        return of(true);
    }

    /**
     * Unlock session
     *
     * @param credentials
     */
    unlockSession(credentials: { email: string; password: string }): Observable<any> {
        return this._httpClient.post('api/auth/unlock-session', credentials);
    }

    /**
     * Check the authentication status
     */
    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            console.log('expired - try to refresh');
            return this.tryToRefreshToken();
        }

        // If the access token exists and it didn't expire, sign in using it
        return of(true);
    }

    signout(): Observable<any> {
        return this._httpClient.delete('api/auth/logout');
    }

    /**
     * Get the current logged in user data
     */
    get(): Observable<UserResource> {
        return this._accessService.loggedUser();
    }


    public profile(): Observable<any> {
        if (this.userSaved) {
            return of(this.userSaved);
        } else if (this.observableUser) {
            return this.observableUser;
        } else {
            this.observableUser = this.get().pipe(
                map((user: any) => this.userSaved = user)
            );
            return this.observableUser;
        }
    }

    public clearLoggedUserCache() {
        this.userSaved = null;
        this.observableUser = null;
        this.profile();
    }

    validatePassword(control: AbstractControl): { [key: string]: any } | null {
        const value: string = control.value;
        const hasNumber = /\d/.test(value);
        const hasUppercase = /[A-Z]/.test(value);
        const hasLowercase = /[a-z]/.test(value);
        const hasMinimumLength = value && value.length >= 8;
        let errorMessage = this.translateService.instant('section.pass.mustContain');
        let error = false;
        if (!hasNumber) {
            error = true;
            errorMessage += this.translateService.instant('section.pass.number');
        }

        if (!hasUppercase) {
            error = true;
            errorMessage += this.translateService.instant('section.pass.uppercase');
        }

        if (!hasLowercase) {
            error = true;
            errorMessage += this.translateService.instant('section.pass.lowercase');
        }

        if (!hasMinimumLength) {
            error = true;
            errorMessage += this.translateService.instant('section.pass.minimumLength');
        }

        if (error) {
            errorMessage = errorMessage.slice(0, -2);
            errorMessage+= '.';
            return { invalidPassword: errorMessage };
        }

        return null;
    }

    checkPasswords(group: FormGroup) {

        if (!group.controls.password || !group.controls.passwordConfirmation) {
            return true;
        }
        const pass = group.controls.password.value;
        const confirmPass = group.controls.passwordConfirmation.value;
        group.controls['passwordConfirmation'].setErrors(pass === confirmPass
            ? group.controls['passwordConfirmation'].errors : {notSame: true});
        return pass === confirmPass ? null : {notSame: true};


    }

}
